// src/components/home/Hero.jsx
import React from "react";
import {
  ArrowRight, Clock3, Plane, MapPin, ShieldCheck,
  Car, Navigation, CircleDot, CarFront,
} from "lucide-react";
import Button from "../common/Button";
import { siteConfig } from "../../homeComponents/data/siteConfig";
import heroRoad from "../../assets/vehicles/banner.png";

const Hero = ({ onBookRide }) => {
  const services = [
    { icon: Clock3, title: "24/7 Service" },
    { icon: Plane, title: "Airport Transfers" },
    { icon: MapPin, title: "Outstation Trips" },
    { icon: ShieldCheck, title: "Safe & Reliable" },
  ];

  const handleExploreMotors = () => {
    document.getElementById("njv-motors")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative overflow-hidden">
      <div className="relative min-h-[620px] w-full sm:min-h-[650px] lg:min-h-[570px]">

        {/* DESKTOP BACKGROUND IMAGE */}
        <img
          src={heroRoad}
          alt="NJV Tours and Travels"
          className="absolute inset-0 hidden h-full w-full object-cover object-center lg:block"
        />

        {/* MOBILE ANIMATED BACKGROUND */}
        <div className="absolute inset-0 lg:hidden">
          <div className="njv-mobile-hero-bg absolute inset-0" />
          <div className="absolute -right-24 top-20 h-72 w-72 animate-pulse rounded-full bg-[#00C878]/20 blur-3xl" />
          <div className="absolute -left-24 bottom-20 h-64 w-64 animate-pulse rounded-full bg-[#8BF108]/10 blur-3xl" style={{ animationDelay: "1s" }} />

          <div className="absolute bottom-0 left-1/2 h-[48%] w-[180%] -translate-x-1/2 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-[#020E20] via-[#061A3A]/90 to-transparent" />

            <div className="absolute left-1/2 top-0 h-full w-[5px] -translate-x-1/2 opacity-70">
              <div className="njv-road-line absolute h-20 w-full rounded-full bg-[#8BF108]" />
              <div className="njv-road-line absolute h-20 w-full rounded-full bg-[#8BF108]" style={{ animationDelay: "0.8s" }} />
              <div className="njv-road-line absolute h-20 w-full rounded-full bg-[#8BF108]" style={{ animationDelay: "1.6s" }} />
              <div className="njv-road-line absolute h-20 w-full rounded-full bg-[#8BF108]" style={{ animationDelay: "2.4s" }} />
            </div>

            <div className="absolute left-[30%] top-0 h-full w-[2px] rotate-[12deg] bg-white/20" />
            <div className="absolute right-[30%] top-0 h-full w-[2px] -rotate-[12deg] bg-white/20" />
          </div>

          <div className="absolute right-8 top-24 animate-bounce opacity-20">
            <Plane className="h-10 w-10 rotate-[-20deg] text-white" />
          </div>
          <div className="absolute left-8 top-40 animate-bounce opacity-20" style={{ animationDelay: "1s" }}>
            <Navigation className="h-8 w-8 text-[#8BF108]" />
          </div>
          <div className="absolute right-16 top-1/2 animate-pulse opacity-20" style={{ animationDelay: "1.5s" }}>
            <CircleDot className="h-7 w-7 text-[#00C878]" />
          </div>
        </div>

        {/* DESKTOP OVERLAY */}
        <div className="absolute inset-0 hidden bg-gradient-to-r from-[#061A3A]/90 via-[#061A3A]/65 to-[#061A3A]/20 lg:block" />

        {/* MOBILE OVERLAY */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#061A3A]/80 via-[#061A3A]/65 to-[#061A3A]/95 lg:hidden" />

        {/* HERO CONTENT */}
        <div className="relative z-10 mx-auto flex min-h-[620px] max-w-[1440px] items-center px-5 py-12 sm:min-h-[650px] sm:px-8 lg:min-h-[570px] lg:px-12 xl:px-16">
          <div className="w-full max-w-3xl lg:max-w-[700px]">

            {/* HERO BRAND + HEADING */}
            <div className="njv-fade-in mb-5">
              {/* Brand eyebrow */}
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#00C878] sm:w-10" />
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#8BF108] sm:text-xs">
                  {siteConfig.hero.tagline || "Travel Anytime • Anywhere"}
                </span>
              </div>
            </div>

            {/* MAIN HEADING */}
            <h1 className="njv-fade-up max-w-3xl text-[44px] font-black leading-[0.92] tracking-[-0.04em] text-white sm:text-6xl lg:text-[72px] xl:text-[78px]">
              <span className="block">Your Journey,</span>
              <span className="mt-1 block">
                Our{" "}
                <span className="relative inline-block text-[#00C878]">
                  Priority.
                  {/* Small underline */}
                  <span className="absolute -bottom-1 left-0 h-[3px] w-2/3 rounded-full bg-[#8BF108] sm:-bottom-2" />
                </span>
              </span>
            </h1>

            {/* SUBTITLE */}
            <p
              className="njv-fade-up mt-6 max-w-xl text-sm font-medium leading-6 text-white/75 sm:mt-7 sm:text-base sm:leading-7 lg:text-lg"
              style={{ animationDelay: "150ms" }}
            >
              {siteConfig.hero.subtitle ||
                "Comfortable travel, reliable vehicles and trusted service for every journey."}
            </p>

            {/* VEHICLE / TRAVEL / MOTORS HIGHLIGHT */}
            <div className="njv-fade-up mt-6 w-full max-w-2xl" style={{ animationDelay: "250ms" }}>
              <div className="grid gap-2.5 sm:gap-3">

                {/* VEHICLES */}
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.07] px-3.5 py-2.5 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/[0.11] sm:px-4 sm:py-3">
                  <div className="shrink-0 rounded-lg bg-[#00C878]/15 px-2.5 py-1.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#8BF108] sm:text-[10px]">
                    Vehicles
                  </div>
                  <p className="min-w-0 text-xs font-semibold leading-5 text-white/85 sm:text-sm">
                    {siteConfig.hero.vehicles || "Rent a Car • Coach • Van • Ertiga • Toyota"}
                  </p>
                </div>

                {/* TRAVEL SERVICES */}
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.07] px-3.5 py-2.5 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/[0.11] sm:px-4 sm:py-3">
                  <div className="shrink-0 rounded-lg bg-[#00A260]/20 px-2.5 py-1.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#00C878] sm:text-[10px]">
                    Travel
                  </div>
                  <p className="min-w-0 text-xs font-semibold leading-5 text-white/85 sm:text-sm">
                    {siteConfig.hero.services || "Airport Transfers • Outstation Trips • Local Travel"}
                  </p>
                </div>

                {/* NJV MOTORS */}
                <div className="group relative overflow-hidden rounded-xl border border-orange-400/30 bg-gradient-to-r from-orange-500/15 via-orange-400/10 to-transparent px-3.5 py-3 backdrop-blur-md transition-all duration-300 hover:border-orange-400/50 hover:from-orange-500/20 sm:px-4 sm:py-3.5">
                  <div className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-orange-500/20 blur-2xl transition-all duration-500 group-hover:bg-orange-500/30" />
                  <div className="relative flex items-center gap-3">
                    <div className="flex shrink-0 items-center gap-1.5 rounded-lg bg-orange-500 px-2.5 py-1.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-white shadow-lg shadow-orange-500/20 sm:text-[10px]">
                      <CarFront className="h-3 w-3" />
                      NJV Motors
                    </div>
                    <p className="min-w-0 text-xs font-bold leading-5 text-white sm:text-sm">
                      {siteConfig.hero.motors || "Buy • Sell • Exchange • Car Consultancy"}
                    </p>
                  </div>
                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-orange-400 transition-all duration-500 group-hover:w-full" />
                </div>

              </div>
            </div>

            {/* KEY TRAVEL BENEFITS */}
            <div className="mt-7 grid max-w-2xl grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={service.title}
                    className="njv-fade-up group flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2.5 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.10] sm:flex-col sm:items-start sm:gap-2 sm:px-3.5 sm:py-3"
                    style={{ animationDelay: `${350 + index * 100}ms` }}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/10 transition-all duration-300 group-hover:border-[#00C878]/40 group-hover:bg-[#00C878]/15 sm:h-10 sm:w-10">
                      <Icon className="h-4 w-4 text-[#8BF108] transition-transform duration-300 group-hover:scale-110 sm:h-[18px] sm:w-[18px]" />
                    </div>
                    <span className="text-[10px] font-bold leading-tight text-white/85 sm:text-xs">
                      {service.title}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* MAIN CTA BUTTONS */}
            <div className="njv-fade-up mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" style={{ animationDelay: "750ms" }}>

              {/* BOOK YOUR RIDE */}
              <Button
                variant="green"
                size="lg"
                onClick={onBookRide}
                icon={ArrowRight}
                iconPosition="right"
                className="group min-h-[52px] w-full rounded-2xl px-6 text-base font-bold shadow-lg shadow-[#00A260]/20 sm:w-auto sm:px-8"
              >
                <Car className="h-5 w-5" />
                <span>Book Your Ride</span>
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>

              {/* EXPLORE NJV MOTORS */}
              <button
                type="button"
                onClick={handleExploreMotors}
                className="group inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-[#F97316] bg-[#F97316] px-6 text-sm font-bold text-white shadow-lg shadow-[#F97316]/25 transition-all duration-300 hover:border-[#EA580C] hover:bg-[#EA580C] hover:shadow-xl hover:shadow-[#F97316]/30 active:scale-[0.98] sm:w-auto sm:px-7"
              >
                <CarFront className="h-5 w-5 text-white transition-transform duration-300 group-hover:scale-110" />
                <span>Explore NJV Motors</span>
                <ArrowRight className="h-4 w-4 text-white transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

          </div>

          {/* DECORATIVE DESKTOP TEXT */}
          <div className="pointer-events-none absolute right-8 top-16 hidden max-w-[230px] text-right lg:block xl:right-16">
            <p className="njv-fade-in font-serif text-4xl font-semibold italic leading-[0.95] text-white drop-shadow-sm xl:text-5xl">
              Travel
              <br />
              Anytime
              <br />
              Anywhere
            </p>
            <div className="ml-auto mt-3 h-1 w-32 rotate-[-4deg] rounded-full bg-[#00C878]" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;